import { useEffect, useState } from "react";
import api, { fmtINR } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Printer, MessageSquare, Search, FileDown, FileUp, Trash2, Pencil, ChevronRight } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { exportToExcel } from "@/lib/excel";
import { tableToPdf, whatsappSend } from "@/lib/pdf";
import { partyBalanceMessage } from "@/lib/messages";
import { usePinPrompt } from "@/components/app/PinPrompt";
import SmartImport from "@/components/app/SmartImport";

const empty = { name: "", kind: "customer", phone: "", email: "", address: "", gstin: "", credit_period_days: 30, opening_balance: 0, opening_type: "none", sites: [] };

export default function Parties() {
  const nav = useNavigate();
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [f, setF] = useState(empty);
  const [editingId, setEditingId] = useState(null);
  const [importOpen, setImportOpen] = useState(false);
  const pinPrompt = usePinPrompt();

  const load = async () => {
    const { data } = await api.get(`/parties${q ? `?q=${encodeURIComponent(q)}` : ""}`);
    setItems(data);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const save = async () => {
    await pinPrompt();
    try {
      if (editingId) {
        await api.patch(`/parties/${editingId}`, f);
        toast.success("Party updated");
      } else {
        await api.post("/parties", f);
        toast.success("Party added");
      }
      setOpen(false); setF(empty); setEditingId(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this party?")) return;
    await pinPrompt();
    try { await api.delete(`/parties/${id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error("Failed"); }
  };

  const edit = (p) => { setF({ ...p, sites: p.sites || [] }); setEditingId(p._id_str); setOpen(true); };

  const addSite = () => setF({ ...f, sites: [...(f.sites || []), { name: `Site ${f.sites.length + 1}`, address: "", contact: "" }] });
  const updSite = (i, k, v) => { const s = [...f.sites]; s[i] = { ...s[i], [k]: v }; setF({ ...f, sites: s }); };
  const rmSite = (i) => setF({ ...f, sites: f.sites.filter((_, idx) => idx !== i) });

  const doExport = () => exportToExcel(items.map(({ _id_str, firm_id, ...r }) => r), "parties.xlsx", "Parties");

  const sendMsg = async (p) => {
    // Fetch party statement to compute net + auto-draft
    try {
      const [stmtRes, firmRes] = await Promise.all([
        api.get(`/parties/${p._id_str}/statement`),
        api.get("/firms/me"),
      ]);
      const draft = partyBalanceMessage(p, stmtRes.data.net, firmRes.data);
      const body = window.prompt(`Message to ${p.name} (edit if needed):`, draft);
      if (!body) return;
      whatsappSend(p.phone, body);
    } catch (e) {
      const body = window.prompt("Message to " + p.name);
      if (body) whatsappSend(p.phone, body);
    }
  };

  const doPdf = () => tableToPdf({
    title: "Parties",
    subtitle: `${items.length} records`,
    columns: [
      { key: "name", label: "Name", w: 130 },
      { key: "kind", label: "Type", w: 60 },
      { key: "phone", label: "Phone", w: 100 },
      { key: "gstin", label: "GSTIN", w: 130 },
      { key: "credit_period_days", label: "Credit d", num: true, w: 60 },
      { key: "opening_balance", label: "Opening", num: true, w: 80 },
    ],
    rows: items,
    filename: "parties.pdf",
  });

  return (
    <div>
      <div className="flex items-end justify-between gap-3 flex-wrap mb-6">
        <div>
          <div className="overline">Parties</div>
          <h1 className="text-3xl font-bold tracking-tight">Customers & Suppliers</h1>
        </div>
        <div className="flex gap-2 flex-wrap no-print">
          <div className="flex items-center gap-2 bg-white border border-border rounded-md px-2">
            <Search size={14} className="text-muted-foreground" />
            <Input className="border-0 shadow-none focus-visible:ring-0 h-9" placeholder="Search..." value={q}
              onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && load()} data-testid="parties-search" />
            <Button size="sm" onClick={load}>Go</Button>
          </div>
          <Button variant="outline" onClick={() => setImportOpen(true)} data-testid="parties-import"><FileUp size={14} /> Import (any Excel)</Button>
          <Button variant="outline" onClick={doExport} data-testid="parties-export"><FileDown size={14} /> Excel</Button>
          <Button variant="outline" onClick={doPdf} data-testid="parties-pdf"><FileDown size={14} /> PDF</Button>
          <Button className="btn-primary" onClick={() => { setF(empty); setEditingId(null); setOpen(true); }} data-testid="parties-add-btn"><Plus size={14} /> Add Party</Button>
        </div>
      </div>

      <div className="bg-white border border-border rounded-lg overflow-x-auto">
        <table className="acc-table w-full">
          <thead><tr><th>Name</th><th>Type</th><th>Phone</th><th>GSTIN</th><th className="num">Credit</th><th className="num">Opening</th><th>Actions</th></tr></thead>
          <tbody>
            {items.length === 0 && <tr><td colSpan={7} className="text-center py-6 text-muted-foreground">No parties. Add your first party.</td></tr>}
            {items.map((p) => (
              <tr key={p._id_str} data-testid={`party-row-${p._id_str}`}>
                <td>
                  <button onClick={() => nav(`/app/parties/${p._id_str}`)} className="font-semibold text-[#064E3B] hover:underline text-left" data-testid={`party-open-${p._id_str}`}>{p.name}</button>
                  {p.sites?.length > 0 && <div className="text-xs text-slate-500">{p.sites.length} sites</div>}
                </td>
                <td><span className="chip chip-slate uppercase">{p.kind}</span></td>
                <td className="font-mono">{p.phone || "—"}</td>
                <td className="font-mono text-xs">{p.gstin || "—"}</td>
                <td className="num">{p.credit_period_days}d</td>
                <td className="num">{fmtINR(p.opening_balance)} <span className="text-xs text-muted-foreground">{p.opening_type}</span></td>
                <td>
                  <div className="flex gap-1">
                    <button className="p-1 hover:bg-slate-100 rounded" onClick={() => sendMsg(p)} data-testid={`party-msg-${p._id_str}`} title="WhatsApp"><MessageSquare size={14} /></button>
                    <button className="p-1 hover:bg-slate-100 rounded" onClick={() => window.open(`/print/party/${p._id_str}`, "_blank")} title="Print"><Printer size={14} /></button>
                    <button className="p-1 hover:bg-slate-100 rounded" onClick={() => edit(p)} data-testid={`party-edit-${p._id_str}`}><Pencil size={14} /></button>
                    <button className="p-1 hover:bg-red-50 rounded text-red-600" onClick={() => del(p._id_str)} data-testid={`party-del-${p._id_str}`}><Trash2 size={14} /></button>
                    <button className="p-1 hover:bg-slate-100 rounded" onClick={() => nav(`/app/parties/${p._id_str}`)}><ChevronRight size={14} /></button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle>{editingId ? "Edit Party" : "Add Party"}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Name*</Label><Input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="party-form-name" /></div>
            <div><Label>Type</Label>
              <select className="w-full border border-border rounded-md h-9 px-2 bg-white" value={f.kind} onChange={(e) => setF({ ...f, kind: e.target.value })} data-testid="party-form-kind">
                <option value="customer">Customer</option>
                <option value="supplier">Supplier</option>
                <option value="both">Both</option>
              </select>
            </div>
            <div><Label>Phone</Label><Input value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} /></div>
            <div><Label>Email</Label><Input value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} /></div>
            <div className="col-span-2"><Label>Address</Label><Input value={f.address} onChange={(e) => setF({ ...f, address: e.target.value })} /></div>
            <div><Label>GSTIN</Label><Input value={f.gstin} onChange={(e) => setF({ ...f, gstin: e.target.value })} /></div>
            <div><Label>Credit period (days)</Label><Input type="number" value={f.credit_period_days} onChange={(e) => setF({ ...f, credit_period_days: +e.target.value })} /></div>
            <div><Label>Opening Balance</Label><Input type="number" value={f.opening_balance} onChange={(e) => setF({ ...f, opening_balance: +e.target.value })} /></div>
            <div><Label>Balance type</Label>
              <select className="w-full border border-border rounded-md h-9 px-2 bg-white" value={f.opening_type} onChange={(e) => setF({ ...f, opening_type: e.target.value })}>
                <option value="none">None</option>
                <option value="receivable">Receivable (they owe us)</option>
                <option value="payable">Payable (we owe them)</option>
              </select>
            </div>

            <div className="col-span-2 border-t border-border pt-3">
              <div className="flex items-center justify-between">
                <Label>Sites / Branches</Label>
                <Button size="sm" variant="outline" onClick={addSite} data-testid="party-add-site"><Plus size={12} /> Add site</Button>
              </div>
              {(f.sites || []).map((s, i) => (
                <div key={i} className="grid grid-cols-4 gap-2 mt-2">
                  <Input placeholder="Site name" value={s.name} onChange={(e) => updSite(i, "name", e.target.value)} />
                  <Input placeholder="Address" value={s.address} onChange={(e) => updSite(i, "address", e.target.value)} />
                  <Input placeholder="Contact" value={s.contact} onChange={(e) => updSite(i, "contact", e.target.value)} />
                  <Button variant="outline" onClick={() => rmSite(i)} className="text-red-600"><Trash2 size={14} /></Button>
                </div>
              ))}
            </div>
          </div>
          <div className="flex justify-end gap-2 mt-4">
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button className="btn-primary" onClick={save} data-testid="party-form-save">Save</Button>
          </div>
        </DialogContent>
      </Dialog>
      <SmartImport target="parties" open={importOpen} onClose={() => setImportOpen(false)} onDone={load} />
    </div>
  );
}
